import { useEffect, useRef } from "react";
import { createChart, CrosshairMode, LineStyle } from "lightweight-charts";

const LINE_COLORS = { ema9: "#F472B6", ema20: "#D97706", ema50: "#007AFF", ema200: "#71717A" };

export default function PriceChart({ data, levels = [], overlays = ["ema20", "ema50"], showBB = false, showSR = true, mode = "candles", height = 460, digits = 5 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data?.candles?.length) return;
    const chart = createChart(ref.current, {
      height, layout: { background: { color: "#FFFFFF" }, textColor: "#52525B", fontFamily: "JetBrains Mono" },
      grid: { vertLines: { color: "#F4F4F5" }, horzLines: { color: "#F4F4F5" } },
      crosshair: { mode: CrosshairMode.Normal }, rightPriceScale: { borderColor: "#E4E4E7" },
      timeScale: { borderColor: "#E4E4E7", timeVisible: true, secondsVisible: false },
      localization: { locale: "en-US", priceFormatter: (p) => p.toFixed(digits) },
    });
    const main = mode === "line"
      ? chart.addLineSeries({ color: "#007AFF", lineWidth: 2 })
      : chart.addCandlestickSeries({ upColor: "#059669", downColor: "#DC2626", borderVisible: false, wickUpColor: "#059669", wickDownColor: "#DC2626" });
    main.setData(mode === "line" ? data.candles.map((c) => ({ time: c.time, value: c.close })) : data.candles);
    overlays.forEach((k) => data[k] && chart.addLineSeries({ color: LINE_COLORS[k], lineWidth: 1, priceLineVisible: false, lastValueVisible: false }).setData(data[k]));
    if (showBB) ["bb_upper", "bb_lower"].forEach((k) => chart.addLineSeries({ color: "#8B5CF6", lineWidth: 1, lineStyle: LineStyle.Dotted, priceLineVisible: false, lastValueVisible: false }).setData(data[k]));
    if (showSR) {
      (data.support || []).forEach((p) => main.createPriceLine({ price: p, color: "#05966955", lineWidth: 1, lineStyle: LineStyle.Dashed, title: "S" }));
      (data.resistance || []).forEach((p) => main.createPriceLine({ price: p, color: "#DC262655", lineWidth: 1, lineStyle: LineStyle.Dashed, title: "R" }));
    }
    levels.forEach((l) => l.price && main.createPriceLine({ price: l.price, color: l.color, lineWidth: l.width || 2, lineStyle: l.dashed ? LineStyle.Dashed : LineStyle.Solid, title: l.title }));
    if (data.markers && mode !== "line") main.setMarkers(data.markers);
    chart.timeScale().setVisibleLogicalRange({ from: data.candles.length - 120, to: data.candles.length + 5 });
    const el = ref.current;
    const ro = new ResizeObserver(() => { if (el.isConnected) chart.applyOptions({ width: el.clientWidth }); });
    ro.observe(el);
    return () => { ro.disconnect(); chart.remove(); };
  }, [data, levels, overlays, showBB, showSR, mode, height, digits]);
  return <div ref={ref} className="w-full" style={{ height }} data-testid="price-chart" />;
}
